import { Dispatch, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchPolicyApplicationsWithStatusUpdatesForClientByClientIdRequest,
  postPolicyApplicationFormSubmissionRequest,
} from '@/model/policyApplicationModel';
import { selectPolicyApplicationModel, selectCachedFormConfig, AppState } from '@client/model';
import { AnyAction } from '@reduxjs/toolkit';

export const usePolicyApplicationFormSubmission = (
  clientId: string,
  clientPolicyApplicationId: string,
  formId: string,
  forClientId: string,
) => {
  const dispatch = useDispatch();

  const { applications, status, error } = useSelector(selectPolicyApplicationModel);
  const application = applications?.find(
    app => app.clientPolicyApplicationId === clientPolicyApplicationId,
  );
  const steps = application?.steps;
  const isPartnerPreUnderwritingForm = clientId !== forClientId;
  
  // Get form metadata from Redux cache (already fetched by other hooks)
  const formConfig = useSelector((state: AppState) => 
    selectCachedFormConfig(state, formId)
  );
  
  // Determine step type from cached form config or fallback
  const stepType = isPartnerPreUnderwritingForm
    ? 'partner-pre-underwriting-form'
    : formConfig?.formMetadata?.formType ?? 'application-form';

  const applicationStep = steps?.find(step => step.stepType === stepType);
  const clientPolicyApplicationFormId = applicationStep?.policyApplicationFormId;
  const clientPolicyApplicationForm = applicationStep?.clientPolicyApplicationForm;

  const shouldFetchClientUnderwritingData =
    applications.length === 0 && status !== 'fetching' && !error;

  useEffect(() => {
    if (shouldFetchClientUnderwritingData) {
      dispatch(
        fetchPolicyApplicationsWithStatusUpdatesForClientByClientIdRequest({
          clientId,
        }),
      );
    }
  }, [dispatch, clientId, shouldFetchClientUnderwritingData]);

  return {
    formId: clientPolicyApplicationFormId,
    form: clientPolicyApplicationForm,
    status,
    error,
  };
};

export function savePolicyApplicationFormSubmission(
  clientId: string,
  clientPolicyApplicationId: string,
  policyApplicationFormId: string,
  submissionData: PolicyFormSubmissionData,
  status: PolicyFormStatus,
  visitedSubsections: string[],
  dispatch: Dispatch<AnyAction>,
  forClientId: string,
) {
  dispatch(
    postPolicyApplicationFormSubmissionRequest({
      clientId,
      forClientId,
      clientPolicyApplicationId,
      policyApplicationFormId,
      submissionData,
      status,
      visitedSubsections,
    }),
  );
}
